import {
	CloseOutlined,
	EyeOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import { Button, InputNumber, Modal, Typography } from 'antd';
import Title from 'antd/es/typography/Title';
import { bankIntegrationApi } from 'Api/bankIntegration';
import { loanRequestApi } from 'Api/loanRequest';
import { Loader } from 'components/Global';
import { useEffect, useState } from 'react';
import { bankMapping } from 'utils/mappings';
import { decrypt, toastText } from 'utils/utils';
import AddPreviewModal from '../AddPreviewModel';
import styles from './index.module.scss';

const encryptionKey: any = process.env.REACT_APP_ENCRYPTION_KEY;

type Props = {
	isOpen: boolean;
	handleCancel: () => void;
	isBankLoading: boolean;
	bankDetailsData: any;
	selectedLoanIds: string[];
	handleBankDetails: any;
	fetchLoanRequests: any;
	currency: 'ZMW' | 'USD';
};

interface BankLoadingState {
	[key: number]: boolean;
}

const AddModal = (props: Props) => {
	const {
		isOpen,
		handleCancel,
		isBankLoading,
		bankDetailsData,
		selectedLoanIds: _loanIds,
		handleBankDetails,
		fetchLoanRequests,
		currency,
	} = props;

	const [loadingStates, setLoadingStates] = useState<BankLoadingState>({});
	const [selectedBank, setSelectedBank] = useState<string | null>(null);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [checkedBankId, setCheckedBankId] = useState<string | null>(null);
	const [selectedLoanIds, setSelectedLoanIds] = useState<string[]>([]);

	const [selectedBankData, setSelectedBankData] = useState<any>(null);
	const [conversionRate, setConversionRate] = useState<number | null>(null);
	const [isSavingConversionRate, setIsSavingConversionRate] =
		useState<boolean>(false);

	const isUSD = currency === 'USD';

	const handleBankSelect = (bankId: string) => {
		const bankData = bankDetailsData.find((item: any) => item.id === bankId);
		if (
			bankData?.bankType === 'STANBIC_BANK' &&
			bankData?.isFundTransfer === true
		) {
			toastText(
				'Stanbic loan payment requires a company account that is not set up for fund transfer.',
				'error',
			);
			return;
		}
		setSelectedBank(bankId);
	};

	const handlePreview = async () => {
		const bankData = bankDetailsData.find(
			(item: any) => item.id === selectedBank,
		);

		if (isUSD) {
			if (
				conversionRate === null ||
				conversionRate === undefined ||
				Number(conversionRate) <= 0
			) {
				toastText('Please enter a valid conversion rate', 'error');
				return;
			}

			if (selectedLoanIds.length === 0) {
				toastText('No loans selected', 'error');
				return;
			}

			try {
				setIsSavingConversionRate(true);
				await loanRequestApi.saveConversionRate({
					loanIds: selectedLoanIds,
					conversionRate: Number(conversionRate),
				});
				setSelectedBankData(bankData);
				setIsPreviewOpen(true);
			} catch (error: unknown) {
				const message =
					(
						error as {
							response?: { data?: { message?: string } };
						}
					)?.response?.data?.message ||
					'Something went wrong while saving the conversion rate';
				toastText(message, 'error');
			} finally {
				setIsSavingConversionRate(false);
			}
			return;
		}

		setSelectedBankData(bankData);
		setIsPreviewOpen(true);
	};

	const handlePreviewCancel = () => {
		setIsPreviewOpen(false);
		setSelectedBank(null);
	};

	const handleProceedToPayment = () => {
		setIsPreviewOpen(false);
	};

	const handleCheckBalance = async (bankId: string) => {
		setCheckedBankId(bankId === checkedBankId ? null : bankId);

		setLoadingStates((prev) => ({
			...prev,
			[bankId]: true,
		}));

		try {
			const response = await bankIntegrationApi.checkBankBalance({
				bankId: bankId,
			});

			const balanceData = bankDetailsData.map((item: any) => {
				if (item.id === bankId) {
					return {
						...item,
						balance: response.data?.data?.balance,
					};
				}
				return item;
			});

			handleBankDetails(balanceData);
		} catch (error: any) {
			const message =
				error?.response?.data?.message ||
				'Something went wrong in checking bank balance';
			toastText(message, 'error');
		} finally {
			setLoadingStates((prev) => ({
				...prev,
				[bankId]: false,
			}));
		}
	};

	useEffect(() => {
		if (bankDetailsData && bankDetailsData.length > 0) {
			setSelectedBank(bankDetailsData[0].id);
		}
	}, []);

	useEffect(() => {
		if (isOpen && _loanIds.length > 0) {
			setSelectedLoanIds(_loanIds);
		}
	}, [isOpen, _loanIds]);

	return (
		<>
			<Modal
				open={isOpen}
				closable={false}
				onCancel={handleCancel}
				width={600}
				footer={
					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-start',
						}}
					>
						<Button
							key="cancel"
							onClick={handleCancel}
							disabled={isSavingConversionRate}
						>
							Cancel
						</Button>

						{selectedBank && (
							<Button
								key="preview"
								onClick={handlePreview}
								type="primary"
								style={{ marginLeft: '10px' }}
								loading={isSavingConversionRate}
								disabled={
									isSavingConversionRate ||
									(isUSD &&
										(conversionRate === null ||
											conversionRate === undefined ||
											Number(conversionRate) <= 0))
								}
							>
								{isUSD ? 'Save and Preview' : 'Preview'}
							</Button>
						)}
					</div>
				}
			>
				<div className={`${styles['modal']} modal`}>
					<div className={styles['modal-header']}>
						<Title level={4}>Select Bank (ZMW)</Title>

						<Button icon={<CloseOutlined />} onClick={handleCancel} />
					</div>
					{isBankLoading && <Loader />}
					{!isBankLoading && (
						<div className={styles['modal-body']}>
							{bankDetailsData &&
								bankDetailsData.length > 0 &&
								bankDetailsData.map((item: any) => {
									const bank = bankMapping[item.bankType];
									return (
										<div
											key={item.id}
											className={`${styles['bank-option']} ${
												selectedBank === item.id ? styles['selected'] : ''
											}`}
											onClick={() => handleBankSelect(item.id)}
										>
											<div className={styles['bank-details']}>
												<img
													src={bank?.logo}
													height={60}
													style={{
														objectFit: 'contain',													}}
													width={120}
													alt={bank?.name}
												/>
												<div
													style={{
														fontSize: '16px',
														marginLeft: '10px',
													}}
												>
													{item.accountNumber
														? decrypt(item.accountNumber, encryptionKey)
														: ''}
												</div>
											</div>

											{checkedBankId === item.id && (
												<div className={styles['balance-info']}>
													<Typography.Text
														type="secondary"
														className={styles['available-balance']}
													>
														{item.balance}
													</Typography.Text>
													<div className={styles['balance-amount']}>
														{bank.balance}
													</div>
												</div>
											)}

											{item.isVerified &&
												(item.bankType === 'FIRST_CAPITAL_BANK' ||
													item.bankType === 'INDO_ZAMBIA_BANK') && (
													<Button
														loading={loadingStates[item.id] || false}
														type="default"
														className={styles['check-balance-button']}
														onClick={(e) => {
															e.stopPropagation();
															if (item.isVerified) {
																handleCheckBalance(item.id);
															}
														}}
														disabled={
															!item.isVerified ||
															!(
																item.bankType === 'FIRST_CAPITAL_BANK' ||
																item.bankType === 'INDO_ZAMBIA_BANK'
															) ||
															loadingStates[item.id] ||
															false
														}
													>
														<EyeOutlined
															style={{
																marginRight: '2px',
															}}
														/>
														Check balance
													</Button>
												)}
										</div>
									);
								})}

							{isUSD && (
								<div className={styles['conversion-rate']}>
									<div className={styles['conversion-rate__note']}>
										<InfoCircleOutlined
											className={styles['conversion-rate__note-icon']}
										/>
										<Typography.Text
											className={styles['conversion-rate__note-text']}
										>
											Please select the conversion rate for the payment. All the
											pending and failed employees net amount will be converted
											to ZMW amount and it will be paid in the ZMW account.
										</Typography.Text>
									</div>
									<div className={styles['conversion-rate__field']}>
										<label
											htmlFor="loan-conversion-rate-input"
											className={styles['conversion-rate__label']}
										>
											Conversion Rate (USD to ZMW)
											<span
												style={{
													color: '#ff4d4f',
													marginLeft: 4,
												}}
											>
												*
											</span>
										</label>
										<InputNumber
											id="loan-conversion-rate-input"
											className={styles['conversion-rate__input']}
											min={0}
											step={0.01}
											placeholder="Enter conversion rate"
											value={conversionRate ?? undefined}
											disabled={isSavingConversionRate}
											onChange={(value) =>
												setConversionRate(
													value === null || value === undefined
														? null
														: Number(value),
												)
											}
										/>
									</div>
								</div>
							)}
						</div>
					)}
				</div>
			</Modal>

			{isPreviewOpen && _loanIds.length > 0 && selectedBank && (
				<AddPreviewModal
					isOpen={isPreviewOpen}
					handleCancel={handlePreviewCancel}
					handleProceedToPayment={handleProceedToPayment}
					loanIds={selectedLoanIds}
					selectedBankId={selectedBank}
					selectedBankData={selectedBankData}
					fetchLoanRequests={fetchLoanRequests}
					currency={currency}
					conversionRate={conversionRate ?? 1}
				/>
			)}
		</>
	);
};

export default AddModal;
